/**
 * TALK — MEMBER TRANSCRIPT GATE
 *
 * Nothing that is not the member's speech may be stored as the member's words.
 *
 * The Realtime pipeline commits an audio segment on server VAD and begins generating
 * immediately; whisper's label for that same segment arrives afterwards. So a content-free
 * segment — room noise, breath, or Arvexis's own voice leaking back into the microphone —
 * still produces text. The batch dictation path already refuses this using Whisper's own
 * `no_speech_prob` / `avg_logprob`, but the Realtime event carries no confidence at all, so
 * the only honest tests available here are structural.
 *
 * Two rules, and nothing else:
 *   1. text with no letters or digits in it is not speech;
 *   2. text captured WHILE Arvexis was audible that closely resembles what she was saying is
 *      her voice coming back, not the member's;
 *   3. text written ENTIRELY in a script this conversation cannot be held in (a Korean news
 *      sign-off appearing in an English session) is the engine labelling noise, not the member.
 *      A test of the writing system, not of the words: any Latin letter anywhere in the
 *      transcription keeps it, so a borrowed foreign word inside a real sentence survives.
 *
 * SCOPE OF RULE 3 — V1 EN/ES SESSION CONTRACT ONLY.
 * This is a bounded consequence of V1 offering exactly two languages: `preferred_language` is
 * validated as `en|es` at registration (`server.py:427`) and every language field in the API
 * carries the pattern `^(en|es)$`. Within that contract, a transcription written mostly in
 * Hangul, Kana, Han, Cyrillic, Greek, Hebrew, Arabic, Thai or Devanagari cannot be the member
 * and is the engine labelling noise.
 *
 * It is NOT a claim that non-Latin speech is invalid. A member who genuinely holds a whole
 * utterance in such a script inside an en/es session WOULD currently be refused — an accepted,
 * recorded limit of V1, not a permanent position. The rule is deliberately confined to a single
 * boolean input (`latinScriptSession`) supplied at one call site, so language-aware routing can
 * later pass `false` — per session, per member or per detected language — and rule 3 simply
 * stops applying. Rules 1 and 2, including the accepted self-utterance protection, do not read
 * that flag and are unaffected by any future multilingual work.
 *
 * SHORTNESS IS NEVER A REASON. "Yeah", "No", "Wait" are real answers and always survive. No
 * phrase blacklist: a member who genuinely says "thank you" must be recorded saying it.
 */

// Same echo-similarity threshold already accepted for prompt-echo rejection on the
// dictation path.
// Measured on the real stored corpus: at 0.6 a genuine mutual farewell ("Un abrazo, chao."
// answering her "un abrazo") scored exactly 0.6 and would have been lost, while every actual
// phantom scored 0.71 or higher. 0.7 separates them with the member's turn protected.
export const ECHO_SIMILARITY_THRESHOLD = 0.7;
// Below this, a candidate is treated as a back-channel and is never echo-tested: too short to
// distinguish a leaked fragment from a genuine one-word answer, so the member wins.
export const ECHO_MIN_CHARS = 6;
export const ECHO_MIN_TOKENS = 2;
// How long after her audio stops that leakage can still be captured.
export const PLAYBACK_OVERLAP_GRACE_MS = 600;

export const normaliseForEcho = (text) =>
  (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9áéíóúüñ\u3040-\u30ff\u4e00-\u9fff\uac00-\ud7af]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

// Ratcliff/Obershelp, the same measure difflib.SequenceMatcher uses on the dictation path.
const similarity = (a, b) => {
  if (!a.length || !b.length) return 0;
  const matches = (x, y) => {
    if (!x.length || !y.length) return 0;
    let best = { i: 0, j: 0, size: 0 };
    const previous = new Map();
    for (let i = 0; i < x.length; i++) {
      const current = new Map();
      for (let j = 0; j < y.length; j++) {
        if (x[i] !== y[j]) continue;
        const size = (previous.get(j - 1) || 0) + 1;
        current.set(j, size);
        if (size > best.size) best = { i: i - size + 1, j: j - size + 1, size };
      }
      previous.clear();
      current.forEach((v, k) => previous.set(k, v));
    }
    if (!best.size) return 0;
    return best.size
      + matches(x.slice(0, best.i), y.slice(0, best.j))
      + matches(x.slice(best.i + best.size), y.slice(best.j + best.size));
  };
  return (2 * matches(a, b)) / (a.length + b.length);
};

/**
 * How much the candidate looks like a piece of what Arvexis was saying.
 * The whole utterance is compared, and so is every window of her words the same length as the
 * candidate — a leaked fragment is a fragment, not the whole line.
 */
export const selfUtteranceScore = (candidate, assistantText) => {
  const said = normaliseForEcho(assistantText);
  const heard = normaliseForEcho(candidate);
  if (!said || !heard) return 0;

  let best = similarity(heard, said);
  const saidWords = said.split(' ');
  const heardWords = heard.split(' ');
  const span = heardWords.length;
  if (span && saidWords.length > span) {
    for (let i = 0; i + span <= saidWords.length; i++) {
      best = Math.max(best, similarity(heard, saidWords.slice(i, i + span).join(' ')));
      if (best >= 1) break;
    }
  }
  return best;
};

// Writing systems Arvexis does not hold conversations in. Presence alone proves nothing —
// a borrowed word is normal speech. DOMINANCE does: a transcription written mostly in a
// script the session cannot be held in is the engine labelling noise.
const NON_LATIN_SCRIPT = /[\u0400-\u04ff\u0370-\u03ff\u0590-\u05ff\u0600-\u06ff\u0900-\u097f\u0e00-\u0e7f\u3040-\u30ff\u4e00-\u9fff\uac00-\ud7af]/g;
const LATIN_LETTER = /[a-záéíóúüñàèìòùâêîôûäëïöüçãõ]/gi;
// Below this share of Latin letters among all letters, the text is not this conversation's.
export const MIN_LATIN_LETTER_SHARE = 0.4;

const isImplausibleScript = (heard) => {
  const foreign = (heard.match(NON_LATIN_SCRIPT) || []).length;
  if (!foreign) return false;
  const latin = (heard.match(LATIN_LETTER) || []).length;
  return latin / (latin + foreign) < MIN_LATIN_LETTER_SHARE;
};

/**
 * @param {string} text                  the transcription the Realtime API returned
 * @param {string[]} assistantTexts      what Arvexis said recently (most recent first)
 * @param {boolean} overlappedPlayback   the segment was captured while she was audible
 * @param {boolean} latinScriptSession   this conversation is being held in en/es
 * @returns {{accept: boolean, reason: string|null, score: number}}
 */
export const evaluateMemberTranscript = ({
  text,
  assistantTexts = [],
  overlappedPlayback = false,
  latinScriptSession = true,
} = {}) => {
  // The script test reads the RAW text: normalising for the echo comparison keeps only the
  // scripts that comparison understands, which would misreport a foreign-script phantom as
  // merely unintelligible.
  if (latinScriptSession && isImplausibleScript(text || '')) {
    return { accept: false, reason: 'implausible_script', score: 0 };
  }

  const heard = normaliseForEcho(text);
  if (!heard) return { accept: false, reason: 'unintelligible', score: 0 };

  const longEnoughToTest =
    heard.length >= ECHO_MIN_CHARS && heard.split(' ').length >= ECHO_MIN_TOKENS;

  if (overlappedPlayback && longEnoughToTest) {
    let score = 0;
    for (const said of assistantTexts) {
      score = Math.max(score, selfUtteranceScore(heard, said));
      if (score >= ECHO_SIMILARITY_THRESHOLD) {
        return { accept: false, reason: 'self_utterance', score: Number(score.toFixed(2)) };
      }
    }
    return { accept: true, reason: null, score: Number(score.toFixed(2)) };
  }

  return { accept: true, reason: null, score: 0 };
};

/**
 * What has to be said back to the session when her own voice was taken for a member turn.
 *
 * The transcript arrives after server VAD has already committed the segment and already begun
 * answering it, so refusing to store the text is only half the repair — the answer to nothing is
 * still spoken. Exactly one verdict triggers this: a measured self-utterance. Anything else,
 * including real speech over her playback, is left completely alone.
 */
export const selfEchoCorrections = (verdict, itemId) => {
  if (!verdict || verdict.accept || verdict.reason !== 'self_utterance') return [];
  const corrections = [{ type: 'response.cancel' }];
  if (itemId) corrections.push({ type: 'conversation.item.delete', item_id: itemId });
  return corrections;
};
